import { CompanyAttributes, OrderDetailAttributes } from "@albatrosdeveloper/ave-models-package";
import { Injectable } from "@angular/core";
import {
  ExportContext,
  ExportFileFormat,
  ExportRange,
  ExportService,
  ExportTaskProcessor,
} from "app/layout/export-overlay";
import ceil from "lodash-es/ceil";
import get from "lodash-es/get";
import { LostSaleService } from "../lost-sale.service";
import {
  LOCALIZED_COMMERCIAL_PROPERTIES,
  LOCALIZED_LOGISTIC_PROPERTIES,
  LOCALIZED_REAL_PROPERTIES,
  LOCALIZED_RESUME_PROPERTIES,
  LOST_SALE_COMMERCIAL_EXPORT_ID,
  LOST_SALE_COMMERCIAL_TITLE,
  LOST_SALE_LOGISTIC_EXPORT_ID,
  LOST_SALE_LOGISTIC_TITLE,
  LOST_SALE_REAL_EXPORT_ID,
  LOST_SALE_REAL_TITLE,
  LOST_SALE_RESUME_EXPORT_ID,
  LOST_SALE_RESUME_TITLE,
} from "./lost-sale.constants";
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";
import { IPaginationResult } from "app/common/interfaces/pagination-result.interface";
import { lastValueFrom } from "rxjs";
import { DecimalStockCompanyPipe } from "app/pipes/decimal-stock-company.pipe";
import { CompanyService } from "app/modules/components/configuration/company/company.service.service";

@Injectable()
export class LostSaleExport {
  constructor(
    private exportService: ExportService,
    private lostSaleService: LostSaleService,
    private companyCurrencyPipe: CompanyCurrencyPipe,
    private companyService: CompanyService
  ) {}

  stockDecimalsDisplay = 0;

  private formatDecimals(value: number | undefined | null): string {
    if (value == null) return "0.000";
    return value.toFixed(this.stockDecimalsDisplay);
  }

  getStockDecimalDisplay(){
   this.stockDecimalsDisplay = this.companyService.getActiveCompany().stockDecimalsDisplay;
  }

  private transformItem(item, textLabel: string) {

    const invoice = item.relatedDocuments?.find(
    (doc) => doc.type === 'INVOICE'
    ) ?? null;


    const invoiceSerial =
    invoice?.serial || invoice?.externalDocument || '-';

    if (textLabel === "Resumen") {
      return {
        trip: item.trip?.id ?? "-",
        numberOrder: this.getNumberOrder(item),
        invoiceDocument: invoiceSerial  ?? "-", // this prop isnt in the model
        sellerToName: item.subRoute?.responsibleName ?? "-",
        subRoute: item.subRoute?.name ?? "-",
        salesOffice: item.salesOffice?.name ?? "-",
        salesGroup: item.soldToDChainUserFields?.BP_SALES_GROUP ?? "-",
        salesZone: item.soldToDChainUserFields?.BP_SALES_ZONE ?? "-",
        shipToExternalKeyCode: item.shipToExternalKeyCode ?? "-",
        shipToName: item.shipToName ?? "-",
        date: ExportTaskProcessor.types.excel.date(item.date),
        loadDateTime: ExportTaskProcessor.types.excel.date(item.loadDateTime),
        materialCode: item.material?.code ?? "-",
        materialName: item.material?.name ?? "-",
        location: this.getLocation(item),
        parallelUnitOfMeasure: item.pararellUnitOfMeasure?.name ?? "-",
        pararellConversionFactor: item.pararellConversionFactor,
        sumSoldQuantity: item.sumSoldQuantity,
        sumStockConfirmedERP: item.sumStockConfirmedERP,
        sumDeliveredQuantity: item.sumDeliveredQuantity,
        sumInvoiceQuantity: item.sumInvoiceQuantity,
        baseUnitOfMeasure: item.baseUnitOfMeasure?.name ?? "-",
        soldUnitOfMeasure: item.pararellUnitOfMeasure?.name ?? "-",
        soldQuantity: this.formatDecimals(item.baseQuantitySuggested),
        soldQuantityParallel: this.formatDecimals(item.pararellQuantitySuggested),
        stockConfirmedERP: this.formatDecimals(item.stockConfirmedERP),
        stockConfirmedERPParallel: this.formatDecimals(item.stockConfirmedPararell),
        deliveredQuantity: this.formatDecimals(item.baseQuantityReceivedByERP),
        deliveredQuantityParallel: this.formatDecimals(item.pararellQuantityReceivedByERP),
        invoiceQuantity: this.formatDecimals(item.invoiceBaseQuantity),
        invoiceQuantityParallel: this.formatDecimals(item?.invoicePararellQuantity),
        lostSale: this.formatDecimals(item.lostSale)
      };
    } else if (textLabel === "Venta Perdida Logística") {
      return {
        trip: item.trip?.id ?? "-",
        numberOrder: this.getNumberOrder(item),
        invoiceDocument: invoiceSerial  ?? "-",
        sellerToName: item.subRoute?.responsibleName ?? "-",
        subRoute: item.subRoute?.name ?? "-",
        salesOffice: item.salesOffice?.name ?? "-",
        salesGroup: item.soldToDChainUserFields?.BP_SALES_GROUP ?? "-",
        salesZone: item.soldToDChainUserFields?.BP_SALES_ZONE ?? "-",
        user: item.user ?? "-",
        date: ExportTaskProcessor.types.excel.date(item.date),
        loadDateTime: ExportTaskProcessor.types.excel.date(item.loadDateTime),
        shipToExternalKeyCode: item.shipToExternalKeyCode ?? "-",
        shipToName: item.shipToName ?? "-",
        deliveryDocument: item.deliveryDocument?.externalDocument ?? "-",
        materialCode: item.material?.code ?? "-",
        materialName: item.material?.name ?? "-",
        baseUnitOfMeasure: item.baseUnitOfMeasure?.name ?? "-",
        parallelUnitOfMeasure: item.pararellUnitOfMeasure?.name ?? "-",
        soldQuantitySuggested: this.formatDecimals(item.baseQuantitySuggested),
        soldQuantitySuggestedUMP: this.formatDecimals(item.pararellQuantitySuggested),
        deliveredQuantity: this.formatDecimals(item.baseQuantityReceivedByERP),
        deliveredQuantityUMP: this.formatDecimals(item.pararellQuantityReceivedByERP),
        lostSale: this.formatDecimals(item.baseLogisticLostSale),
        lostSaleUMP: this.formatDecimals(item.pararellLogisticLostSale),
      };
    } else if (textLabel === "Venta Perdida Real") {
      return {
        trip: item.trip?.id ?? "-",
        subRoute: item.subRoute?.name ?? "-",
        salesOffice: item.salesOffice?.name ?? "-",
        salesGroup: item.soldToDChainUserFields?.BP_SALES_GROUP ?? "-",
        salesZone: item.soldToDChainUserFields?.BP_SALES_ZONE ?? "-",
        user: item.user ?? "-",
        date: ExportTaskProcessor.types.excel.date(item.date),
        loadDateTime: ExportTaskProcessor.types.excel.date(item.loadDateTime),
        numberOrder: this.getNumberOrder(item),
        invoiceDocument: invoiceSerial  ?? "-",
        sellerToName: item.subRoute?.responsibleName ?? "-",
        shipToExternalKeyCode: item.shipToExternalKeyCode ?? "-",
        shipToName: item.shipToName ?? "-",
        materialName: item.material?.name ?? "-",
        soldUnitOfMeasure: item.baseUnitOfMeasure?.name ?? "-",
        parallelUnitOfMeasure: item.pararellUnitOfMeasure?.name ?? "-",
        soldQuantitySuggested: this.formatDecimals(item.baseQuantitySuggested),
        soldQuantitySuggestedUMP: this.formatDecimals(item.pararellQuantitySuggested),
        invoiceQuantity: this.formatDecimals(item.invoiceBaseQuantity),
        invoiceQuantityUMP: this.formatDecimals(item.invoicePararellQuantity),
        lostSale: this.formatDecimals(item.baseRealLostSale),
        lostSaleUMP: this.formatDecimals(item.pararellRealLostSale),
      };
    } else {
      return {
        trip: item.trip?.id ?? "-",
        numberOrder: this.getNumberOrder(item),
        invoiceDocument: invoiceSerial  ?? "-",
        sellerToName: item.subRoute?.responsibleName ?? "-",
        subRoute: item.subRoute?.name ?? "-",
        salesOffice: item.salesOffice?.name ?? "-",
        salesGroup: item.soldToDChainUserFields?.BP_SALES_GROUP ?? "-",
        salesZone: item.soldToDChainUserFields?.BP_SALES_ZONE ?? "-",
        user: item.user ?? "-",
        date: ExportTaskProcessor.types.excel.date(item.date),
        loadDateTime: ExportTaskProcessor.types.excel.date(item.loadDateTime),
        shipToExternalKeyCode: item.shipToExternalKeyCode ?? "-",
        shipToName: item.shipToName ?? "-",
        materialCode: item.material?.code ?? "-",
        materialName: item.material?.name ?? "-",
        soldUnitOfMeasure: item.soldUnitOfMeasure?.name ?? "-",
        pararellUnitOfMeasure: item.pararellUnitOfMeasure?.name ?? "-",
        soldQuantitySuggested: this.formatDecimals(item.baseQuantitySuggested),
        soldQuantitySuggestedUMP: this.formatDecimals(item.pararellQuantitySuggested),
        soldQuantity: this.formatDecimals(item.stockConfirmedERP),
        soldQuantityUMP: this.formatDecimals(item.stockConfirmedPararell),
        stockAtCreation: this.formatDecimals(item.baseStockAtCreation),
        stockAtCreationUMP: this.formatDecimals(item.pararellStockAtCreation),
        lostSale: this.formatDecimals(item.baseLostSale),
        lostSaleUMP: this.formatDecimals(item.pararellLostSale),
      };
    }
  }

  private getLocation(item: OrderDetailAttributes): string {
    const locationTwoName = get(item, "locationTwo.name", "").trim();
    const locationThreeName = get(item, "locationThree.name", "").trim();
    return (
      [locationTwoName, locationThreeName].filter(Boolean).join(" - ") || "??"
    );
  }

  private getNumberOrder(item: OrderDetailAttributes): string {
    const numberOrder = get(item, "numberOrder", "").trim();
    const serials = get(item, "serials", "").trim();
    return [numberOrder, serials].filter(Boolean).join(" - ") || "??";
  }

  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    textLabel: string,
    options,
    filter
  ) {
    if(range === ExportRange.AllPages && totalItems >= 20000) {
      pageSize = 1000;
    }
    const totalPages = Math.ceil(totalItems / pageSize);
    this.exportService
      .task({
        id:
          textLabel === "Resumen"
            ? LOST_SALE_RESUME_EXPORT_ID
            : textLabel === "Venta Perdida Logística"
              ? LOST_SALE_LOGISTIC_EXPORT_ID
              : textLabel === "Venta Perdida Real"
                ? LOST_SALE_REAL_EXPORT_ID
                : LOST_SALE_COMMERCIAL_EXPORT_ID,
        title:
          textLabel === "Resumen"
            ? LOST_SALE_RESUME_TITLE
            : textLabel === "Venta Perdida Logística"
              ? LOST_SALE_LOGISTIC_TITLE
              : textLabel === "Venta Perdida Real"
                ? LOST_SALE_REAL_TITLE
                : LOST_SALE_COMMERCIAL_TITLE,
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            textLabel === "Resumen"
              ? LOST_SALE_RESUME_TITLE
              : textLabel === "Venta Perdida Logística"
                ? LOST_SALE_LOGISTIC_TITLE
                : textLabel === "Venta Perdida Real"
                  ? LOST_SALE_REAL_TITLE
                  : LOST_SALE_COMMERCIAL_TITLE,
            textLabel === "Resumen"
              ? LOCALIZED_RESUME_PROPERTIES
              : textLabel === "Venta Perdida Logística"
                ? LOCALIZED_LOGISTIC_PROPERTIES
                : textLabel === "Venta Perdida Real"
                  ? LOCALIZED_REAL_PROPERTIES
                  : LOCALIZED_COMMERCIAL_PROPERTIES
          );
        },
        next: async (ctx: ExportContext) => {
          this.getStockDecimalDisplay();
          options.currentPage = range === ExportRange.AllPages ? ctx.iteration : pageIndex;
          options.limitPerPage = pageSize;
          // options.limitPerPage = range === ExportRange.AllPages ? totalItems : options.limitPerPage;

          const data = [];

          data.push(...await this.getList(options, filter))

          data.map((order: any) => {
            if (textLabel === "Venta Perdida Logística") {
              const requested = order?.soldQuantitySuggested ?? 0;
              const delivered = order?.deliveredQuantity ?? 0;
              order.lostSale = Math.max(0, requested - delivered);
            } else if (textLabel === "Venta Perdida Real") {
              const requested = order?.soldQuantitySuggested ?? 0;
              const invoiced = order?.invoiceQuantity ?? 0;
              order.lostSale = Math.max(0, requested - invoiced);
            }

            if (textLabel === "Venta Perdida Comercial") {
              let soldQuantitySuggestedUMP = parseFloat(
                (
                  (order?.soldQuantitySuggested ?? 0) *
                  (order?.pararellConversionFactor ?? 1)
                ).toFixed(2)
              );
              let soldQuantityUMP = parseFloat(
                (
                  (order?.soldQuantity ?? 0) *
                  (order?.pararellConversionFactor ?? 1)
                ).toFixed(2)
              );
              let stockAtCreationUMP = parseFloat(
                (
                  (order?.stockAtCreation ?? 0) *
                  (order?.pararellConversionFactor ?? 1)
                ).toFixed(2)
              );
              let lostSaleUMP = parseFloat(
                (
                  (order?.lostSale ?? 0) *
                  (order?.pararellConversionFactor ?? 1)
                ).toFixed(2)
              );

              order = {
                ...order,
                soldQuantitySuggestedUMP: soldQuantitySuggestedUMP,
                soldQuantityUMP: soldQuantityUMP,
                stockAtCreationUMP: stockAtCreationUMP,
                lostSaleUMP: lostSaleUMP,
              };
            } else if (textLabel === "Venta Perdida Logística") {
              let soldQuantitySuggestedUMP = parseFloat(
                (
                  (order?.soldQuantitySuggested ?? 0) *
                  (order?.pararellConversionFactor ?? 1)
                ).toFixed(2)
              );
              let deliveredQuantityUMP = parseFloat(
                (
                  (order?.deliveredQuantity ?? 0) *
                  (order?.pararellConversionFactor ?? 1)
                ).toFixed(2)
              );
              order = {
                ...order,
                soldQuantitySuggestedUMP: soldQuantitySuggestedUMP,
                deliveredQuantityUMP: deliveredQuantityUMP,
                lostSaleUMP: Math.max(
                  0,
                  soldQuantitySuggestedUMP - deliveredQuantityUMP
                ),
              };
            } else if (textLabel === "Venta Perdida Real") {
              let soldQuantitySuggestedUMP = parseFloat(
                (
                  (order?.soldQuantitySuggested ?? 0) *
                  (order?.pararellConversionFactor ?? 1)
                ).toFixed(2)
              );
              let invoiceQuantityUMP = parseFloat(
                (
                  (order?.invoiceQuantity ?? 0) *
                  (order?.pararellConversionFactor ?? 1)
                ).toFixed(2)
              );
              order = {
                ...order,
                soldQuantitySuggestedUMP: soldQuantitySuggestedUMP,
                invoiceQuantityUMP: invoiceQuantityUMP,
                lostSaleUMP: Math.max(
                  0,
                  soldQuantitySuggestedUMP - invoiceQuantityUMP
                ),
              };
            }

            ctx.processor.addToSection(
              textLabel === "Resumen"
                ? LOST_SALE_RESUME_TITLE
                : textLabel === "Venta Perdida Logística"
                  ? LOST_SALE_LOGISTIC_TITLE
                  : textLabel === "Venta Perdida Real"
                    ? LOST_SALE_REAL_TITLE
                    : LOST_SALE_COMMERCIAL_TITLE,
              this.transformItem(order, textLabel)
            );
          });
          ctx.next();
        },
      })
      .start();
  }

  canStartTask(textLabel: string) {
    if (
      this.exportService.taskIsActive(
        textLabel === "Resumen"
          ? LOST_SALE_RESUME_EXPORT_ID
          : textLabel === "Venta Perdida Logística"
            ? LOST_SALE_LOGISTIC_EXPORT_ID
            : textLabel === "Venta Perdida Real"
              ? LOST_SALE_REAL_EXPORT_ID
              : LOST_SALE_COMMERCIAL_EXPORT_ID
      )
    ) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }

  private getLocalizedSummaryProps(): Record<string, string> {
    const currencyCode = this.companyCurrencyPipe.transform('code');
    const suffix = currencyCode ? ` (${currencyCode})` : '';
    return {
      ...LOCALIZED_RESUME_PROPERTIES,
      sumSoldQuantity: `Suma Pedido${suffix}`,
      sumStockConfirmedERP: `Suma Reserva${suffix}`,
      sumDeliveredQuantity: `Suma Desp${suffix}`,
      sumInvoiceQuantity: `Suma Factura${suffix}`
    };
  }

  async getList(options, filter) {
    try {
      const res = await lastValueFrom(
        this.lostSaleService.findAllWithCount(options, filter)
      );
      return res?.data?.[0]?.data ?? [];
    } catch (err) {
      return [];
    }
  }

  async getListResume(options, filter) {
    try {
      const res = await lastValueFrom(
        this.lostSaleService.findAllResumeWithCount(options, filter)
      );
      return res?.data?.[0]?.data ?? [];
    } catch (err) {
      return [];
    }
  }
}
